/**
 * Language Detection Utility
 *
 * Detects the user's preferred language from URL query parameters
 * and provides utilities for language-related operations.
 */

import { captureException } from "@/utils/error-tracking";

export type SupportedLanguage = "en" | "es" | "fr" | "pt" | "uk";

export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = [
	"en",
	"es",
	"fr",
	"pt",
	"uk",
];

/**
 * Default BCP-47 tag to send to backend APIs when we only know the Lingui
 * locale code (e.g. fallback path with no URL hint). Partners that pass an
 * `app.languages` priority list have their *original* tag preserved instead.
 */
const DEFAULT_BCP47_TAG: Record<SupportedLanguage, string> = {
	en: "en-US",
	es: "es-US",
	fr: "fr-FR",
	pt: "pt-PT",
	uk: "uk-UA",
};

/**
 * Strict locale-tag matcher: returns the supported locale a raw BCP-47-style
 * tag maps to, or `null` if none. Each language prefix only matches when it
 * sits at a locale boundary — i.e. the next character is end-of-string or one
 * of `- _ ;` — so "english", "frank", "estonian" etc. do not match.
 */
function matchSupportedLanguageStrict(raw: string): SupportedLanguage | null {
	const normalized = raw.toLowerCase().trim();
	if (!normalized) return null;
	const hasBoundary = (prefix: string) => {
		if (!normalized.startsWith(prefix)) return false;
		const next = normalized.charAt(prefix.length);
		return next === "" || next === "-" || next === "_" || next === ";";
	};
	if (hasBoundary("es")) return "es";
	if (hasBoundary("fr")) return "fr";
	if (hasBoundary("pt")) return "pt";
	if (hasBoundary("uk")) return "uk";
	if (hasBoundary("en")) return "en";
	return null;
}

/**
 * Maps API or free-form locale strings (e.g. /prepare `lang`, BCP-47 tags) to a supported Lingui locale.
 * Lenient — unsupported values default to English.
 */
export function parseSupportedLanguageFromString(
	raw: string,
): SupportedLanguage {
	return matchSupportedLanguageStrict(raw) ?? "en";
}

/**
 * Result shape of priority/back-compat URL parsing. `lingui` is the Lingui
 * catalog code (2-letter); `tag` is the *original* BCP-47-style entry from
 * the URL that won the match — preserving regional info partners care about
 * (e.g. `es-MX` vs `es-US`). Backend `userLanguage` fields must use `tag`,
 * Lingui activation uses `lingui`.
 */
export type ResolvedLocale = {
	lingui: SupportedLanguage;
	tag: string;
};

/**
 * Parses a comma-separated, Accept-Language-style priority list (the value of
 * the `app.languages` URL param) and returns the first entry that maps to a
 * supported locale, along with the original BCP-47 tag of that entry. Returns
 * `null` if no entry matches.
 */
/**
 * Strip Accept-Language quality weights (`;q=…`) and any other parameters
 * that come after the BCP-47 tag itself. `es-MX;q=0.9` → `es-MX`.
 */
function stripQualityWeight(rawEntry: string): string {
	const trimmed = rawEntry.trim();
	const semi = trimmed.indexOf(";");
	return semi >= 0 ? trimmed.slice(0, semi).trim() : trimmed;
}

export function parseAppLanguagesParam(raw: string): ResolvedLocale | null {
	if (!raw) return null;
	for (const rawEntry of raw.split(",")) {
		const tag = stripQualityWeight(rawEntry);
		const match = matchSupportedLanguageStrict(tag);
		if (match) return { lingui: match, tag };
	}
	return null;
}

/**
 * Detects language using URL params with the following precedence:
 * 1. `app.languages` priority list — first entry mapping to a supported locale
 * 2. `lang` single-value param (back-compat)
 * 3. Browser language preference
 * 4. English fallback
 *
 * Returns both the Lingui code and the original BCP-47 tag so callers can
 * activate the catalog *and* round-trip the precise regional value back to
 * the backend in `userLanguage` fields.
 *
 * The `source` field marks where the locale came from so downstream code
 * (e.g. PrepareInitializer) can avoid overwriting a URL-driven choice with
 * a stale backend value.
 */
export type LocaleResolution = ResolvedLocale & {
	source: "url-app-languages" | "url-lang" | "browser" | "default";
};

export function detectLocaleFromUrl(): LocaleResolution {
	if (typeof window === "undefined") {
		return { lingui: "en", tag: DEFAULT_BCP47_TAG.en, source: "default" };
	}

	try {
		const urlParams = new URLSearchParams(window.location.search);

		const appLanguages = urlParams.get("app.languages");
		if (appLanguages) {
			const resolved = parseAppLanguagesParam(appLanguages);
			if (resolved) {
				return { ...resolved, source: "url-app-languages" };
			}
		}

		const langParam = urlParams.get("lang");
		if (langParam) {
			const trimmed = stripQualityWeight(langParam);
			const match = matchSupportedLanguageStrict(trimmed);
			if (match) {
				return { lingui: match, tag: trimmed, source: "url-lang" };
			}
			// Unsupported value — fall back to en but record we saw a lang
			return {
				lingui: "en",
				tag: DEFAULT_BCP47_TAG.en,
				source: "url-lang",
			};
		}

		const browser = detectBrowserLocale();
		if (browser) {
			return { ...browser, source: "browser" };
		}

		return { lingui: "en", tag: DEFAULT_BCP47_TAG.en, source: "default" };
	} catch (error) {
		captureException(error, {
			tags: { component: "language-detection", action: "detectLocaleFromUrl" },
		});
		console.warn("Error detecting locale from URL:", error);
		return { lingui: "en", tag: DEFAULT_BCP47_TAG.en, source: "default" };
	}
}

/**
 * Back-compat shim: returns just the Lingui code from `detectLocaleFromUrl`.
 * Existing call sites that only need the catalog code (e.g. `main.tsx`
 * `initializeI18n`) can keep using this.
 */
export function detectLanguagesFromUrl(): SupportedLanguage {
	return detectLocaleFromUrl().lingui;
}

function detectBrowserLocale(): ResolvedLocale | null {
	if (typeof navigator === "undefined") return null;
	try {
		const prefs =
			navigator.languages?.length > 0
				? navigator.languages
				: [navigator.language || ""];
		for (const pref of prefs) {
			const match = matchSupportedLanguageStrict(pref);
			if (match) return { lingui: match, tag: pref };
		}
	} catch (error) {
		captureException(error, {
			tags: {
				component: "language-detection",
				action: "detectBrowserLocale",
			},
		});
		console.warn("Error detecting browser language:", error);
	}
	return null;
}

/**
 * Gets the browser's preferred language as a fallback
 *
 * @returns The browser's language preference (a SupportedLanguage)
 */
export function getBrowserLanguage(): SupportedLanguage {
	return detectBrowserLocale()?.lingui ?? "en";
}

/**
 * Returns a sensible default BCP-47 tag for a given Lingui locale, used when
 * we don't have a partner-supplied tag to round-trip (e.g. browser-fallback
 * path, or callers that only have the Lingui code in hand).
 */
export function getDefaultLocaleTag(lingui: SupportedLanguage): string {
	return DEFAULT_BCP47_TAG[lingui];
}

/**
 * Checks if a language code is supported
 *
 * @param lang - The language code to check
 * @returns True if the language is supported
 */
export function isSupportedLanguage(lang: string): lang is SupportedLanguage {
	return (SUPPORTED_LANGUAGES as readonly string[]).includes(lang);
}

/**
 * Gets the display name for a language code
 *
 * @param lang - The language code
 * @returns The display name of the language
 */
export function getLanguageDisplayName(lang: SupportedLanguage): string {
	switch (lang) {
		case "es":
			return "Español";
		case "fr":
			return "Français";
		case "pt":
			return "Português";
		case "uk":
			return "Українська";
		default:
			return "English";
	}
}

/**
 * Gets the locale identifier for Lingui
 *
 * @param lang - The language code
 * @returns The locale identifier for Lingui
 */
export function getLanguageLocale(lang: SupportedLanguage): string {
	return lang; // For now, language codes match locale identifiers
}
